import { combineTransactionSteps, findChildrenInRange, getChangedRanges } from "@tiptap/core"
import { Emoji as BaseEmoji, emojiToShortcode } from "@tiptap/extension-emoji"
import { Plugin } from "@tiptap/pm/state"
import emojiRegex from "emoji-regex"

// `emoji-regex` also matches text-presentation symbols (↔ © ™ →). Only
// characters that render as emoji by default, or that carry the emoji
// variation selector (↔️), should be swapped for an emoji node.
const EMOJI_PRESENTATION = /\p{Emoji_Presentation}|\uFE0F/u

export function isEmojiPresentation(text) {
  return EMOJI_PRESENTATION.test(text)
}

/**
 * @tiptap/extension-emoji with its unicode-to-emoji-node conversion limited to
 * real emoji. Everything else (suggestion, paste rules, rendering) is upstream.
 */
export const Emoji = BaseEmoji.extend({
  addProseMirrorPlugins() {
    const plugins = this.parent?.() || []

    return plugins.map((plugin) => {
      if (typeof plugin.spec?.appendTransaction !== "function") return plugin

      return new Plugin({
        ...plugin.spec,
        appendTransaction: (transactions, oldState, newState) =>
          replaceEmojiText({
            editor: this.editor,
            emojis: this.options.emojis,
            type: this.type,
            transactions,
            oldState,
            newState,
          }),
      })
    })
  },
})

// Mirrors the upstream appendTransaction, plus the presentation guard.
function replaceEmojiText({ editor, emojis, type, transactions, oldState, newState }) {
  if (editor.view.composing) return

  const docChanges =
    transactions.some((transaction) => transaction.docChanged) && !oldState.doc.eq(newState.doc)
  if (!docChanges) return

  const { tr } = newState
  const transform = combineTransactionSteps(oldState.doc, transactions)
  const changes = getChangedRanges(transform)

  changes.forEach(({ newRange }) => {
    if (newState.doc.resolve(newRange.from).parent.type.spec.code) return

    const textNodes = findChildrenInRange(newState.doc, newRange, (node) => node.type.isText)

    textNodes.forEach(({ node, pos }) => {
      if (!node.text) return

      const matches = [...node.text.matchAll(emojiRegex())]

      matches.forEach((match) => {
        if (match.index === undefined) return

        const emoji = match[0]
        if (!isEmojiPresentation(emoji)) return

        const name = emojiToShortcode(emoji, emojis)
        if (!name) return

        const from = tr.mapping.map(pos + match.index)
        if (newState.doc.resolve(from).parent.type.spec.code) return

        const to = from + emoji.length
        tr.replaceRangeWith(from, to, type.create({ name }))
        tr.setStoredMarks(newState.doc.resolve(from).marks())
      })
    })
  })

  if (!tr.steps.length) return

  return tr
}

export default Emoji
